import React from 'react';
import { Container } from 'react-bootstrap';
import Navbar from './Navbar';
import Footer from './Footer';

const Privacy: React.FC = () => {
  return (
    <>
      <Navbar />
      <Container className="py-5">
        <h1 className="mb-4">Privacy Policy</h1>
        <p className="text-muted">Last updated: {new Date().toLocaleDateString()}</p>

        <section className="my-4">
          <p>
            Defined Home Services LLC ("Company," "we," "us," or "our") is committed to protecting your privacy.
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use
            our DHS CRM platform and related services (collectively, the "Service").
          </p>
          <p>
            By using the Service, you agree to the collection and use of information in accordance with this Privacy
            Policy. If you do not agree with our policies and practices, do not use the Service.
          </p>
        </section>

        <section className="my-4">
          <h2>1. Information We Collect</h2>

          <h3>1.1 Information You Provide to Us</h3>
          <p>We collect information you provide directly to us, including:</p>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, phone number, company name, business address, and password when you create an account.</li>
            <li><strong>Profile Information:</strong> Job title, department, profile photo, and other information you add to your profile.</li>
            <li><strong>Payment Information:</strong> Credit card number, billing address, and other payment details (processed securely through third-party payment processors).</li>
            <li><strong>Customer Data:</strong> Information you enter about your customers, appointments, invoices, projects, and other business data.</li>
            <li><strong>Communications:</strong> Messages, feedback, support requests, and other communications you send to us.</li>
            <li><strong>User Content:</strong> Photos, documents, files, notes, and other content you upload or create within the Service.</li>
          </ul>

          <h3>1.2 Information We Collect Automatically</h3>
          <p>When you access or use our Service, we automatically collect certain information, including:</p>
          <ul>
            <li><strong>Usage Information:</strong> Features you use, pages you view, time spent on pages, search queries, and other actions you take.</li>
            <li><strong>Device Information:</strong> IP address, browser type and version, operating system, device type, unique device identifiers, and mobile network information.</li>
            <li><strong>Location Information:</strong> Approximate location based on your IP address (precise location only with your permission).</li>
            <li><strong>Cookies and Similar Technologies:</strong> We use cookies, web beacons, and similar tracking technologies to collect information about your browsing activities. See our Cookie Policy below for details.</li>
            <li><strong>Log Data:</strong> Server logs containing information such as IP address, browser type, pages visited, time and date of visits, time spent on pages, and other diagnostic data.</li>
          </ul>

          <h3>1.3 Information from Third Parties</h3>
          <p>We may receive information about you from third parties, including:</p>
          <ul>
            <li><strong>Authentication Services:</strong> If you log in using Google, Microsoft, or other third-party authentication services.</li>
            <li><strong>Payment Processors:</strong> Payment confirmation and transaction details from our payment processing partners.</li>
            <li><strong>Marketing Partners:</strong> Contact information and other data from marketing and lead generation partners (only with your consent).</li>
            <li><strong>Analytics Providers:</strong> Aggregated usage statistics and analytics data.</li>
            <li><strong>Integration Partners:</strong> Data from third-party services you choose to connect with our Service (e.g., accounting software, email marketing platforms).</li>
          </ul>
        </section>

        <section className="my-4">
          <h2>2. How We Use Your Information</h2>
          <p>We use the information we collect for the following purposes:</p>

          <h3>2.1 To Provide and Maintain the Service</h3>
          <ul>
            <li>Create and manage your account</li>
            <li>Process transactions and send transaction notifications</li>
            <li>Provide customer support and respond to your requests</li>
            <li>Deliver the features and functionality you request</li>
            <li>Store and manage your Customer Data</li>
          </ul>

          <h3>2.2 To Improve and Develop the Service</h3>
          <ul>
            <li>Understand how users interact with the Service</li>
            <li>Conduct research and analysis to improve our features</li>
            <li>Develop new products, services, and features</li>
            <li>Test and monitor the effectiveness of the Service</li>
            <li>Diagnose and fix technical issues</li>
          </ul>

          <h3>2.3 To Communicate with You</h3>
          <ul>
            <li>Send service-related announcements and updates</li>
            <li>Respond to your comments, questions, and support requests</li>
            <li>Send promotional communications, newsletters, and marketing materials (with your consent)</li>
            <li>Request feedback and conduct surveys</li>
            <li>Provide information about features, updates, and special offers</li>
          </ul>

          <h3>2.4 For Security and Compliance</h3>
          <ul>
            <li>Detect, prevent, and address fraud, security issues, and technical problems</li>
            <li>Enforce our Terms of Service and other policies</li>
            <li>Comply with legal obligations and respond to lawful requests</li>
            <li>Protect our rights, privacy, safety, and property</li>
            <li>Conduct security audits and risk assessments</li>
          </ul>

          <h3>2.5 For Analytics and Personalization</h3>
          <ul>
            <li>Analyze usage patterns and trends</li>
            <li>Personalize your experience and provide tailored content</li>
            <li>Measure the effectiveness of our marketing campaigns</li>
            <li>Create aggregated, de-identified data for business intelligence</li>
          </ul>
        </section>

        <section className="my-4">
          <h2>3. Legal Basis for Processing (GDPR)</h2>
          <p>If you are located in the European Economic Area (EEA), UK, or Switzerland, we process your personal data based on the following legal grounds:</p>
          <ul>
            <li><strong>Contract Performance:</strong> Processing is necessary to perform our contract with you (providing the Service).</li>
            <li><strong>Legitimate Interests:</strong> Processing is necessary for our legitimate business interests, such as improving the Service, preventing fraud, and ensuring security.</li>
            <li><strong>Consent:</strong> You have given explicit consent for specific processing activities (e.g., marketing communications).</li>
            <li><strong>Legal Obligation:</strong> Processing is necessary to comply with legal obligations.</li>
          </ul>
        </section>

        <section className="my-4">
          <h2>4. How We Share Your Information</h2>
          <p>We do not sell, rent, or trade your personal information. We may share your information in the following circumstances:</p>

          <h3>4.1 Service Providers</h3>
          <p>
            We share information with third-party service providers who perform services on our behalf, such as:
          </p>
          <ul>
            <li>Cloud hosting and infrastructure providers (e.g., AWS, Google Cloud)</li>
            <li>Payment processors (e.g., Stripe, PayPal)</li>
            <li>Email service providers</li>
            <li>Analytics providers (e.g., Google Analytics)</li>
            <li>Customer support platforms</li>
            <li>Marketing and advertising partners</li>
          </ul>
          <p>
            These service providers are contractually obligated to protect your information and use it only for the purposes we specify.
          </p>

          <h3>4.2 Business Transfers</h3>
          <p>
            If we are involved in a merger, acquisition, reorganization, sale of assets, or bankruptcy, your information may be
            transferred as part of that transaction. We will notify you before your information is transferred and becomes subject
            to a different privacy policy.
          </p>

          <h3>4.3 Legal Requirements</h3>
          <p>We may disclose your information if required to do so by law or in response to:</p>
          <ul>
            <li>Legal process (subpoena, court order, or other legal request)</li>
            <li>Governmental or regulatory inquiries</li>
            <li>Requests from law enforcement or other government agencies</li>
            <li>Legal claims or disputes</li>
          </ul>

          <h3>4.4 Protection of Rights</h3>
          <p>
            We may disclose information when we believe it is necessary to protect our rights, property, or safety, or the rights,
            property, or safety of our users or the public.
          </p>

          <h3>4.5 With Your Consent</h3>
          <p>
            We may share information with third parties when you give us explicit consent to do so, such as when you authorize
            integrations with other services.
          </p>

          <h3>4.6 Aggregated and De-Identified Data</h3>
          <p>
            We may share aggregated, anonymized, or de-identified information that cannot reasonably be used to identify you for
            research, marketing, analytics, or other purposes.
          </p>
        </section>

        <section className="my-4">
          <h2>5. Cookies and Tracking Technologies</h2>
          <p>We use cookies and similar tracking technologies to collect and store information. Types of cookies we use include:</p>

          <h3>5.1 Essential Cookies</h3>
          <p>
            Required for the Service to function properly. These cookies enable core functionality such as security, authentication,
            and network management. You cannot opt out of these cookies.
          </p>

          <h3>5.2 Functional Cookies</h3>
          <p>
            Enable enhanced functionality and personalization, such as remembering your preferences, language settings, and region.
          </p>

          <h3>5.3 Analytics Cookies</h3>
          <p>
            Help us understand how visitors interact with the Service by collecting and reporting information anonymously. We use
            services like Google Analytics to track usage patterns and improve the Service.
          </p>

          <h3>5.4 Advertising Cookies</h3>
          <p>
            Used to deliver relevant advertisements and track advertising campaign performance. These cookies may track your
            browsing activity across different websites.
          </p>

          <h3>5.5 Managing Cookies</h3>
          <p>
            Most web browsers allow you to control cookies through their settings. You can set your browser to refuse cookies or
            alert you when cookies are being sent. However, disabling cookies may affect the functionality of the Service.
          </p>
          <p>You can also opt out of certain tracking:</p>
          <ul>
            <li>Google Analytics: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">https://tools.google.com/dlpage/gaoptout</a></li>
            <li>Network Advertising Initiative: <a href="http://www.networkadvertising.org/choices/" target="_blank" rel="noopener noreferrer">http://www.networkadvertising.org/choices/</a></li>
          </ul>
        </section>

        <section className="my-4">
          <h2>6. Data Security</h2>
          <p>
            We implement appropriate technical and organizational security measures to protect your information against unauthorized
            access, alteration, disclosure, or destruction. Our security measures include:
          </p>
          <ul>
            <li>Encryption of data in transit using SSL/TLS protocols</li>
            <li>Encryption of sensitive data at rest</li>
            <li>Regular security assessments and vulnerability testing</li>
            <li>Access controls and authentication mechanisms</li>
            <li>Employee training on data protection and security</li>
            <li>Secure backup and disaster recovery procedures</li>
            <li>Network security measures including firewalls and intrusion detection</li>
          </ul>
          <p>
            However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use
            commercially acceptable means to protect your information, we cannot guarantee absolute security. You are responsible
            for maintaining the confidentiality of your account credentials.
          </p>
        </section>

        <section className="my-4">
          <h2>7. Data Retention</h2>
          <p>
            We retain your information for as long as necessary to provide the Service, comply with legal obligations, resolve
            disputes, and enforce our agreements. Specific retention periods include:
          </p>
          <ul>
            <li><strong>Account Information:</strong> Retained while your account is active and for a reasonable period after account closure.</li>
            <li><strong>Customer Data:</strong> Retained while your account is active. You can delete Customer Data at any time through the Service.</li>
            <li><strong>Transaction Records:</strong> Retained for at least 7 years to comply with tax and accounting regulations.</li>
            <li><strong>Communications:</strong> Retained for as long as necessary to provide support and resolve issues.</li>
            <li><strong>Analytics Data:</strong> Typically retained for 26 months in aggregated form.</li>
          </ul>
          <p>
            When we no longer need your information, we will securely delete or anonymize it. You can request deletion of your
            data at any time, subject to our legal retention requirements.
          </p>

          <h3>7.1 Account Deletion Policy</h3>
          <p>
            When you request deletion of your account, we initiate a 30-day grace period during which your account is deactivated
            but not permanently deleted. During this grace period:
          </p>
          <ul>
            <li>Your account is temporarily suspended and you cannot access the Service</li>
            <li>You may log in at any time during the 30-day period to reactivate your account</li>
            <li>Logging in during this period will cancel the deletion request and fully restore your account</li>
            <li>If you do not log in within the 30-day period, your account and all associated data will be permanently deleted</li>
          </ul>
          <p>
            After the 30-day grace period expires without account reactivation, all your personal information and Customer Data
            will be permanently deleted from our systems, except for information we are required to retain for legal, tax, or
            regulatory purposes (such as transaction records). This deletion is irreversible and your data cannot be recovered.
          </p>
        </section>

        <section className="my-4">
          <h2>8. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your country of residence. These countries
            may have different data protection laws than your jurisdiction.
          </p>
          <p>
            If you are located in the EEA, UK, or Switzerland, we ensure that transfers of personal data to third countries are
            protected by appropriate safeguards, such as:
          </p>
          <ul>
            <li>Standard Contractual Clauses approved by the European Commission</li>
            <li>Adequacy decisions by the European Commission</li>
            <li>EU-U.S. Data Privacy Framework (if applicable)</li>
            <li>Other legally approved transfer mechanisms</li>
          </ul>
        </section>

        <section className="my-4">
          <h2>9. Your Rights and Choices</h2>

          <h3>9.1 General Rights</h3>
          <p>You have the following rights regarding your personal information:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information.</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information, subject to certain exceptions.</li>
            <li><strong>Objection:</strong> Object to processing of your information for certain purposes.</li>
            <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances.</li>
            <li><strong>Data Portability:</strong> Request a copy of your information in a structured, machine-readable format.</li>
          </ul>

          <h3>9.2 GDPR Rights (EEA, UK, Switzerland)</h3>
          <p>If you are located in the EEA, UK, or Switzerland, you have additional rights under the GDPR:</p>
          <ul>
            <li>Right to withdraw consent at any time (where processing is based on consent)</li>
            <li>Right to lodge a complaint with your local data protection authority</li>
            <li>Right to object to processing based on legitimate interests</li>
            <li>Right not to be subject to automated decision-making, including profiling</li>
          </ul>

          <h3>9.3 CCPA Rights (California Residents)</h3>
          <p>If you are a California resident, you have the following rights under the California Consumer Privacy Act (CCPA):</p>
          <ul>
            <li><strong>Right to Know:</strong> Request disclosure of the categories and specific pieces of personal information we have collected about you.</li>
            <li><strong>Right to Delete:</strong> Request deletion of your personal information, subject to certain exceptions.</li>
            <li><strong>Right to Opt-Out:</strong> Opt out of the sale of your personal information (we do not sell personal information).</li>
            <li><strong>Right to Non-Discrimination:</strong> You have the right not to receive discriminatory treatment for exercising your CCPA rights.</li>
          </ul>

          <h3>9.4 Marketing Communications</h3>
          <p>
            You can opt out of receiving promotional emails by clicking the "unsubscribe" link in any marketing email or by
            adjusting your account settings. Even if you opt out of marketing communications, we may still send you service-related
            messages (e.g., account notifications, security alerts).
          </p>

          <h3>9.5 Exercising Your Rights</h3>
          <p>
            To exercise any of these rights, please contact us at info@thedefinedcrm.com. We will respond to your request within
            the timeframe required by applicable law (typically 30 days). We may need to verify your identity before processing
            your request.
          </p>
        </section>

        <section className="my-4">
          <h2>10. Children's Privacy</h2>
          <p>
            Our Service is not intended for children under the age of 18, and we do not knowingly collect personal information
            from children under 18. If you are under 18, do not use the Service or provide any information to us.
          </p>
          <p>
            If we learn that we have collected personal information from a child under 18 without parental consent, we will delete
            that information as quickly as possible. If you believe we may have collected information from a child under 18, please
            contact us at info@thedefinedcrm.com.
          </p>
        </section>

        <section className="my-4">
          <h2>11. Third-Party Links and Services</h2>
          <p>
            The Service may contain links to third-party websites, applications, or services that are not owned or controlled by us.
            We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy policies
            of any third-party services before providing them with your information.
          </p>
          <p>
            When you integrate third-party services with our Service (e.g., accounting software, payment processors), those services
            may collect information about you according to their own privacy policies.
          </p>
        </section>

        <section className="my-4">
          <h2>12. Do Not Track Signals</h2>
          <p>
            Some web browsers have a "Do Not Track" (DNT) feature that signals to websites that you do not want to have your online
            activity tracked. Our Service does not currently respond to DNT signals or similar mechanisms. We continue to work with
            industry groups to define a common understanding of how to respond to DNT signals.
          </p>
        </section>

        <section className="my-4">
          <h2>13. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements,
            or other factors. We will notify you of any material changes by:
          </p>
          <ul>
            <li>Posting the updated Privacy Policy on our website</li>
            <li>Updating the "Last updated" date at the top of this Privacy Policy</li>
            <li>Sending an email notification to the address associated with your account (for material changes)</li>
            <li>Displaying a prominent notice within the Service</li>
          </ul>
          <p>
            Your continued use of the Service after the effective date of the updated Privacy Policy constitutes your acceptance
            of the changes. If you do not agree to the updated Privacy Policy, you must stop using the Service and may request
            deletion of your account.
          </p>
          <p>
            We encourage you to review this Privacy Policy periodically to stay informed about our information practices.
          </p>
        </section>

        <section className="my-4">
          <h2>14. California Shine the Light Law</h2>
          <p>
            California Civil Code Section 1798.83 permits California residents to request certain information about our disclosure
            of personal information to third parties for their direct marketing purposes. We do not share personal information with
            third parties for their direct marketing purposes without your consent.
          </p>
        </section>

        <section className="my-4">
          <h2>15. Nevada Privacy Rights</h2>
          <p>
            Nevada residents have the right to opt out of the sale of certain covered information collected by website operators.
            We do not sell your covered information as defined under Nevada law. If you have questions about our data practices,
            please contact us at info@thedefinedcrm.com.
          </p>
        </section>

        <section className="my-4">
          <h2>16. Contact Us and Data Protection Officer</h2>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
          </p>
          <p>
            <strong>Defined Home Services LLC</strong><br />
            Email: info@thedefinedcrm.com<br />
            Address: [Your Physical Address]<br />
            Phone: [Your Phone Number]
          </p>
          <p>
            <strong>Data Protection Officer:</strong><br />
            Email: dpo@thedefinedcrm.com
          </p>
          <p>
            We will respond to all inquiries within a reasonable timeframe, typically within 30 days.
          </p>

          <h3>EU Representative</h3>
          <p>
            If you are located in the EEA or UK and have concerns about our data practices, you can also contact our EU representative:
          </p>
          <p>
            [EU Representative Name and Contact Information - if applicable]
          </p>

          <h3>Supervisory Authority</h3>
          <p>
            If you are located in the EEA, UK, or Switzerland, you have the right to lodge a complaint with your local data
            protection authority if you believe we have violated your privacy rights.
          </p>
        </section>

        <section className="my-4">
          <p className="text-muted small">
            By using the Service, you acknowledge that you have read this Privacy Policy, understand it, and agree to be bound by it.
            If you do not agree to this Privacy Policy, you are not authorized to use the Service.
          </p>
        </section>
      </Container>
      <Footer />
    </>
  );
};

export default Privacy;
